import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Container,
  Flex,
  Image,
  Text,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { motion } from "framer-motion"

import faqTitle from "#/assets/faq-title.svg"
import mazeBg from "#/assets/maze-background.png"
import {
  LANDING_CONTAINER_WIDTH,
  LANDING_FAQ_REGEX,
  routes,
} from "#/shared/constants"
import AnimatedCarousel from "#/ui-old/Animation/AnimatedCarousel"

type FAQItem = {
  title: string
  paragraph: string
}

type LandingFaqProps = {
  faq?: FAQItem[]
  faqHeader?: string
  legal1?: string
  legal2?: string
  companyList: { content: JSX.Element }[]
}

export default function HomeFaq({
  faq = [],
  faqHeader = "",
  legal1 = "",
  legal2 = "",
  companyList,
}: LandingFaqProps) {
  return (
    <motion.div
      initial="hidden"
      transition={{ type: "spring", stiffness: 100, damping: 15 }}
      whileInView="visible"
      viewport={{ once: true, amount: 0.0 }}
      variants={{
        hidden: { y: -25 },
        visible: { y: -200 },
      }}
    >
      <Text
        fontSize={{ base: "12px", md: "14px" }}
        mb={{ base: "-10px", md: "-40px" }}
        mt="120px"
        color="primary.black.100"
        textTransform="uppercase"
        textAlign="center"
        fontFamily="riforma.monoRegular"
      >
        Friends who share our vision.
      </Text>
      <AnimatedCarousel items={companyList} />
      <motion.div
        initial="hidden"
        transition={{ type: "spring", stiffness: 100, damping: 15 }}
        whileInView="visible"
        viewport={{ once: true, amount: 0.0 }}
        variants={{
          hidden: { y: 120, opacity: 0 },
          visible: { y: 0, opacity: 1 },
        }}
      >
        <Box
          bg={`url(${mazeBg})`}
          backgroundPosition="center"
          height="600px"
          w={{ base: "90%", md: "100%" }}
          maxWidth={LANDING_CONTAINER_WIDTH}
          margin="40px auto"
          borderRadius="25px"
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            p={{ base: "40px 20px", md: "60px" }}
          >
            <Text
              as="h2"
              fontSize="40px"
              lineHeight="120%"
              textAlign={{ base: "center", md: "center" }}
              letterSpacing="-2px"
            >
              Experience Freedom
            </Text>
            <Text
              mt="20px"
              color=" #014A7A"
              maxW="400px"
              textAlign={{ base: "center", md: "center" }}
            >
              {" "}
              Borrow, earn, and spend without selling a single sat.
              <br /> No banks. No middlemen.
              <br /> Just Bitcoin, working for you.
            </Text>
            <Box display={{ base: "none", md: "block" }}>
              <Link to={routes.EXPLORE}>
                <Button
                  variant="primary"
                  className="steps-signup"
                  bg="black"
                  color="white"
                  p="10px 40px"
                  m="30px auto"
                  h={{ base: "0", md: "60px" }}
                  fontSize="18px"
                  borderRadius="25px"
                  fontFamily="riforma.medium"
                  fontWeight="500"
                  _hover={{
                    color: "white",
                    background: "primary.red.100",
                  }}
                >
                  Get Started
                </Button>
              </Link>
            </Box>
          </Flex>
        </Box>
      </motion.div>
      <Container
        maxWidth={LANDING_CONTAINER_WIDTH}
        display="flex"
        justifyContent="space-between"
        flexDirection="column"
      >
        <motion.div
          initial="hidden"
          transition={{ type: "spring", stiffness: 100, damping: 15 }}
          whileInView="visible"
          viewport={{ once: true, amount: 0.0 }}
          variants={{
            hidden: { y: 120, opacity: 0 },
            visible: { y: 0, opacity: 1 },
          }}
        >
          <Box
            maxWidth="100%"
            padding={{ base: 0, md: 10 }}
            pt={{ base: 0, md: "20px" }}
          >
            <Image
              src={faqTitle}
              w="200px"
              maxWidth="300px"
              margin="30px 0"
              alt={faqHeader}
            />
            {faq.length > 0 && (
              <Accordion allowToggle>
                {faq.map((item) => (
                  <AccordionItem
                    key={item.title}
                    border="none"
                    mb={0}
                    borderBottom="1px solid #e4e4d4"
                  >
                    <AccordionButton
                      p="30px"
                      fontSize={{ base: "16px", md: "20px" }}
                      fontFamily="riforma.medium"
                      _expanded={{}}
                      data-attr={`faq-${item.title
                        .toLowerCase()
                        .split(LANDING_FAQ_REGEX)
                        .filter((el) => el !== "")
                        .join("-")}`}
                    >
                      <Box flex="1" textAlign="left" as="h3">
                        {item.title}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel
                      p={6}
                      fontSize={{ base: "16px", md: "20px" }}
                      borderBottomRadius="16px"
                      dangerouslySetInnerHTML={{ __html: item.paragraph }}
                    />
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </Box>
        </motion.div>
      </Container>
      <Text
        maxWidth="920px"
        margin="20px auto"
        textAlign="center"
        fontWeight={{ base: "normal", md: "bold" }}
        px={4}
      >
        {legal1}
      </Text>
      <Text
        maxWidth="1080px"
        margin="20px auto"
        textAlign="center"
        fontWeight={{ base: "normal", md: "bold" }}
        px={4}
      >
        {legal2}
      </Text>
    </motion.div>
  )
}
